import React, { useCallback, useMemo, useRef, useState } from 'react';
import { CustomVizDefinition } from 'libs/end-to-end/src/custom-viz-definition';
import { CustomVizPollResponse } from 'libs/end-to-end/src/custom-viz/CustomVizDefinitions';
import { ObservationLocationType } from 'libs/end-to-end/src/observation-upload-type';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { errResult } from 'libs/end-to-end/src/result';
import { okResult } from 'libs/end-to-end/src/result';
import { Result } from 'libs/end-to-end/src/result';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { UserAuthenticatedApiCall } from 'apps/sigma2-ui/src/state/user-types';
import { useCustomVizDoViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomVizDoViz';
import { CustomVizContext } from 'apps/sigma2-ui/src/components/custom-viz/CustomVizContext';
import { useCustomVizDropdownOptions } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomVizDropdownOptions';
import { useCustomVizStatus } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomVizStatus';
import { usePolling } from 'apps/sigma2-ui/src/lib/hooks/usePolling';
import { useUserAnalysisSignaling } from 'apps/sigma2-ui/src/state/user-analysis';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { jsonParse } from 'libs/end-to-end/src/utils/utility/json-parse';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { CustomVizCompletedAnalyticsEvent } from 'libs/end-to-end/src/analytics/CustomVizCompletedAnalyticsEvent';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { mapCustomVizRequestToAnalyticsEventProperties } from 'apps/sigma2-ui/src/components/custom-viz/CustomVizHelpers';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { useAlive } from 'apps/sigma2-ui/src/lib/hooks/useAlive';

export const CustomViz = (props: {
  observation: ObservationLocationType;
  organizationId: string;
  customVizDefinition: CustomVizDefinition;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  children: React.ReactNode;
}) => {
  const {
    observation,
    organizationId,
    customVizDefinition,
    userAuthenticatedApiCall,
    children,
  } = props;

  const analytics = useAnalytics();

  const [result, setResult] = useState<Result<unknown | null, BaseError>>(
    okResult(null),
  );

  const [resultCreatedAt, setResultCreatedAt] = useState<number | null>(null);

  const [staleResult, setStaleResult] = useState<
    Result<unknown | null, BaseError>
  >(okResult(null));

  const [staleResultCreatedAt, setStaleResultCreatedAt] = useState<
    number | null
  >(null);

  const [startTime, setStartTime] = useState<Date>(new Date());

  const currentRequestRef = useRef<CustomVizRequest | null>(null);

  const [currentRequest, privateSetCurrentRequest] =
    useState<CustomVizRequest | null>(null);

  const setCurrentRequest = useCallback(
    (customVizRequest: CustomVizRequest | null) => {
      privateSetCurrentRequest(customVizRequest);
      currentRequestRef.current = customVizRequest;
    },
    [],
  );

  const [
    hasCurrentCustomVizPolledMultipleTimes,
    setHasCurrentCustomVizPolledMultipleTimes,
  ] = useState(false);

  const [isLoadingDropdownOptions, setIsLoadingDropdownOptions] =
    useState(false);

  const dropdownOptions = useCustomVizDropdownOptions(
    useMemo(() => {
      return {
        customVizDefinition,
        setIsLoadingDropdownOptions,
        observation,
        organizationId,
        userAuthenticatedApiCall,
      };
    }, [
      customVizDefinition,
      observation,
      organizationId,
      userAuthenticatedApiCall,
    ]),
  );

  // The initial state here should disappear if we make `CustomViz` and `useCustomViz` into a hook without any context.
  const [lastSuccessfulCustomVizRequest, setLastSuccessfulCustomVizRequest] =
    useState<CustomVizRequest | null>(null);

  const vizInProgress = useMemo(() => !!currentRequest, [currentRequest]);

  const status = useCustomVizStatus(
    useMemo(() => {
      return {
        vizInProgress,
        isLoadingDropdownOptions,
        staleResult,
      };
    }, [isLoadingDropdownOptions, vizInProgress, staleResult]),
  );

  const userAnalysisSignaling = useUserAnalysisSignaling();
  const isAlive = useAlive();

  const pollingCallback = useMemo(() => {
    return async () => {
      const request = currentRequestRef.current;
      if (!isAlive() || !request) {
        return;
      }

      const eventProperties = mapCustomVizRequestToAnalyticsEventProperties({
        request,
      });

      const processPollResult = await processDoVizPoll({
        isAlive,
        userAuthenticatedApiCall,
        request,
        setResult,
        setResultCreatedAt,
        setStaleResult,
        setStaleResultCreatedAt,
        customVizDefinition,
        setLastSuccessfulCustomVizRequest,
      });

      if (processPollResult === 'pending') {
        setHasCurrentCustomVizPolledMultipleTimes(true);
      } else {
        let eventResult: 'Request Succeeded' | 'Request Failed' | 'Not Alive';
        if (processPollResult === 'not-alive') {
          eventResult = 'Not Alive';
        } else if (processPollResult === 'success') {
          eventResult = 'Request Succeeded';
        } else if (processPollResult === 'failure') {
          eventResult = 'Request Failed';
        } else {
          throw guardExhaustive(processPollResult);
        }

        const endTime = new Date();

        const durationInSeconds =
          (endTime.getTime() - startTime.getTime()) / 1000;

        const event: CustomVizCompletedAnalyticsEvent = {
          eventName: 'Custom Viz Completed',
          eventProperties: {
            ...eventProperties,
            $duration: durationInSeconds,
            result: eventResult,
          },
        };

        analytics.trackEvent(event);

        userAnalysisSignaling.sendSignal(customVizDefinition);

        if (request === currentRequestRef.current) {
          setCurrentRequest(null);
        }
      }
    };
  }, [
    analytics,
    customVizDefinition,
    isAlive,
    setCurrentRequest,
    startTime,
    userAnalysisSignaling,
    userAuthenticatedApiCall,
  ]);

  const doViz = useCustomVizDoViz({
    userAuthenticatedApiCall,
    customVizDefinition,
    onError: useCallback(
      (error) => {
        setStaleResult(okResult(null));
        setStaleResultCreatedAt(null);
        setResult(errResult(error));
        setResultCreatedAt(null);
        setCurrentRequest(null);
      },
      [setCurrentRequest, setStaleResult, setResult],
    ),
    beforeRequestStarted: useCallback(() => {
      setLastSuccessfulCustomVizRequest(null);
      setHasCurrentCustomVizPolledMultipleTimes(false);
      setResult(okResult(null));
      setResultCreatedAt(null);
      setStaleResult(okResult(null));
      setStaleResultCreatedAt(null);
    }, []),
    afterRequestStarted: useCallback(
      ({ startingTookMultipleAttempts }) => {
        userAnalysisSignaling.sendSignal(customVizDefinition);
        setStartTime(new Date());
        setHasCurrentCustomVizPolledMultipleTimes(
          startingTookMultipleAttempts ? true : false,
        );
        pollingCallback();
      },
      [customVizDefinition, pollingCallback, userAnalysisSignaling],
    ),
    onStaleResultLoaded: useCallback(
      ({
        currentRequest,
        staleResultForRequest,
        staleResultCreatedAtForRequest,
      }: {
        currentRequest: CustomVizRequest;
        staleResultForRequest: unknown;
        staleResultCreatedAtForRequest: number | null;
      }) => {
        setStaleResult(okResult(staleResultForRequest));
        setStaleResultCreatedAt(staleResultCreatedAtForRequest);
        setLastSuccessfulCustomVizRequest(currentRequest);
      },
      [],
    ),
    setVizInProgressExecuting: setCurrentRequest,
    getVizInProgressExecuting: useCallback(() => {
      return currentRequestRef.current;
    }, []),
  });

  const pollCurrent = useMemo(() => {
    return async () => {
      if (!currentRequest) return;

      await pollingCallback();
    };
  }, [currentRequest, pollingCallback]);

  usePolling({
    action: pollCurrent,
    delay: 5000,
    // We do our state setting in the action itself.
    onReceivedResult: () => {},
  });

  const cancelViz = useCallback(() => {
    setCurrentRequest(null);
  }, [setCurrentRequest]);

  // TODO: Rename this and clean up.
  const r = useMemo(() => {
    if (
      result.resultType === 'err' ||
      (result.resultType === 'ok' && result.value !== null)
    ) {
      return result;
    }
    return staleResult;
  }, [result, staleResult]);

  const rCreatedAt = useMemo(() => {
    if (
      result.resultType === 'err' ||
      (result.resultType === 'ok' && result.value !== null)
    ) {
      return resultCreatedAt;
    }
    return staleResultCreatedAt;
  }, [result, resultCreatedAt, staleResultCreatedAt]);

  return (
    <CustomVizContext.Provider
      value={{
        doViz,
        result: r,
        resultCreatedAt: rCreatedAt,
        staleResult,
        staleResultCreatedAt,
        status,
        dropdownOptions,
        lastSuccessfulCustomVizRequest,
        cancelViz,
        currentInProgressCustomVizRequest: currentRequest,
        hasCurrentCustomVizPolledMultipleTimes,
      }}
    >
      {children}
    </CustomVizContext.Provider>
  );
};

const processDoVizPoll = async (props: {
  isAlive: () => boolean;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  request: CustomVizRequest;
  setResult: Setter<Result<unknown | null, BaseError>>;
  setResultCreatedAt: Setter<number | null>;
  setStaleResult: Setter<Result<unknown | null, BaseError>>;
  setStaleResultCreatedAt: Setter<number | null>;
  customVizDefinition: CustomVizDefinition;
  setLastSuccessfulCustomVizRequest: Setter<CustomVizRequest | null>;
}): Promise<'success' | 'pending' | 'failure' | 'not-alive'> => {
  const {
    isAlive,
    userAuthenticatedApiCall,
    request,
    setResult,
    setResultCreatedAt,
    setStaleResult,
    setStaleResultCreatedAt,
    customVizDefinition,
    setLastSuccessfulCustomVizRequest,
  } = props;
  const pollResult = await userAuthenticatedApiCall({
    responseSchema: CustomVizPollResponse,
    url: `${getApiBaseUrl()}/v2/custom-viz/poll`,
    requestOptions: {
      method: 'POST',
      body: JSON.stringify(request),
    },
  });

  if (!isAlive() || !pollResult) {
    return 'not-alive';
  }

  if (pollResult.resultType === 'err') {
    setStaleResult(okResult(null));
    setStaleResultCreatedAt(null);
    setResult(errResult(pollResult.error));
    setResultCreatedAt(null);
    return 'failure';
  }

  const pollResponse = pollResult.value;

  if (pollResponse.pollStatus === 'pending') {
    return 'pending';
  }

  if (pollResponse.pollStatus === 'fail') {
    setStaleResult(okResult(null));
    setStaleResultCreatedAt(null);
    setResult(
      errResult({
        errorType: 'RunCustomVizDataError',
        errorReason: 'Poll response failed',
      }),
    );
    setResultCreatedAt(pollResponse.createdAt);

    return 'failure';
  }

  const outputParseResult = jsonParse(
    pollResponse.outputContents,
    customVizDefinition.outputSchema,
  );

  if (outputParseResult.resultType === 'err') {
    setStaleResult(okResult(null));
    setStaleResultCreatedAt(null);
    setResult(errResult(outputParseResult.error));
    setResultCreatedAt(pollResponse.createdAt);
    return 'failure';
  } else {
    setStaleResult(okResult(null));
    setStaleResultCreatedAt(null);
    setResult(okResult(outputParseResult.value));
    setResultCreatedAt(pollResponse.createdAt);
    setLastSuccessfulCustomVizRequest(request);
  }

  return 'success';
};
